import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ShieldCheck, PenLine } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useGuestJSA, useSubmitGuestSign } from '@/hooks/use-safety';
import { JSAHazardCarousel, type HazardAcknowledgement } from '@/components/safety/jsa-hazard-carousel';
import { SignaturePad } from '@/components/safety/signature-pad';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import {
  PlanInitialsFields,
  type PlanInitialRevision,
} from '@/app/components/safety/plan-initials-fields';
import type { PlanInitialInput } from '@/lib/types';

export function JSAGuestSignPage() {
  const { token } = useParams<{ token: string }>();

  // The link alone grants nothing for emailed invitations: the server answers
  // with 403 until the invited email is confirmed.  In-person kiosk links are
  // exempt and load straight away.
  const [emailInput, setEmailInput] = useState('');
  const [confirmedEmail, setConfirmedEmail] = useState('');

  const { data, isLoading, error } = useGuestJSA(token || '', confirmedEmail);
  const signMutation = useSubmitGuestSign(token || '');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [signed, setSigned] = useState(false);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [acknowledgementData, setAcknowledgementData] = useState<HazardAcknowledgement[] | null>(null);
  const [planInitials, setPlanInitials] = useState<PlanInitialInput[]>([]);

  // Pre-fill name from server once loaded
  const [prefilled, setPrefilled] = useState(false);
  if (data && !prefilled) {
    if (data.guest.name) setName(data.guest.name);
    setPrefilled(true);
  }

  // If this JSA has no hazards, no swipe acknowledgement is required.
  useEffect(() => {
    if (data && data.jsa.hazards.length === 0) {
      setAcknowledgementData([]);
    }
  }, [data]);

  const handleSign = () => {
    if (!acknowledgementData || !signatureData) return;
    signMutation.mutate(
      {
        email: confirmedEmail,
        name,
        phone: phone || undefined,
        signature_data: signatureData,
        acknowledgement_data: acknowledgementData,
        plan_initials: planInitials,
      },
      {
        onSuccess: (result) => {
          setSigned(true);
          setSignedAt(result.signed_at);
        },
      }
    );
  };

  const challengeFailed =
    (error as { response?: { status?: number } } | null)?.response?.status === 403;
  const challengeRejected =
    challengeFailed &&
    (error as { response?: { data?: { code?: string } } })?.response?.data?.code ===
      'email_mismatch';

  if (challengeFailed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 items-center text-center">
            <ShieldCheck className="h-8 w-8 text-blue-600" />
            <CardTitle className="text-lg">Confirm your email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              Enter the email address this signing link was sent to. The JSA is only
              shown after your email is confirmed.
            </p>
            <div className="space-y-2">
              <Label htmlFor="guest-email">Email address</Label>
              <Input
                id="guest-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            {challengeRejected && (
              <p className="text-sm text-red-600">
                {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                  'That email address does not match this signing link.'}
              </p>
            )}
            <Button
              className="w-full"
              disabled={!emailInput.trim() || isLoading}
              onClick={() => setConfirmedEmail(emailInput.trim())}
            >
              {isLoading ? 'Checking…' : 'Continue'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } };
    const isExpired = axiosError?.response?.status === 410;
    const isNotFound = axiosError?.response?.status === 404;
    const message = axiosError?.response?.data?.error;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isExpired ? 'Link Expired' : isNotFound ? 'Link Not Found' : 'Unable to Load JSA'}
            </h2>
            <p className="text-gray-500">
              {message || (isExpired
                ? 'This signing link has expired. Please contact the person who sent you this link for a new one.'
                : isNotFound
                ? 'This signing link is invalid or does not exist.'
                : 'There was a problem loading the JSA. Please try again or contact support.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { jsa, guest, organization_time_zone: timeZone } = data;
  const signPlans: PlanInitialRevision[] = (jsa.plan_revisions ?? []).map((plan) => ({
    plan_type: plan.plan_type,
    id: plan.id,
    title: plan.title,
    version: plan.version,
    url: plan.url,
    initialled_at: plan.initialled_at,
  }));

  const alreadySigned = guest.has_signed || signed;

  if (alreadySigned) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900">JSA Signed</h2>
            <p className="text-gray-500">
              {guest.has_signed
                ? `This JSA was already signed on ${formatDateTimeInTimeZone(guest.signed_at!, timeZone)}.`
                : `Thank you. Your signature has been recorded${signedAt ? ` at ${formatDateTimeInTimeZone(signedAt, timeZone)}` : ''}.`}
            </p>
            <p className="text-sm text-gray-400">You can now close this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            <img src="/logos/h3ight.svg" alt="H3ight" className="h-5" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{jsa.title}</h1>
          <p className="text-gray-500 mt-1">{jsa.location} · {jsa.date}</p>
        </div>

        {/* Scope of work */}
        <Card>
          <CardHeader><CardTitle className="text-base">Scope of Work</CardTitle></CardHeader>
          <CardContent>
            {jsa.scope_of_work ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{jsa.scope_of_work}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">Not documented</p>
            )}
          </CardContent>
        </Card>

        {/* Hazards */}
        <Card>
          <CardHeader><CardTitle className="text-base">Hazard Analysis</CardTitle></CardHeader>
          <CardContent>
            {jsa.hazards.length > 0 ? (
              <JSAHazardCarousel hazards={jsa.hazards} onComplete={setAcknowledgementData} />
            ) : (
              <p className="text-sm text-gray-500 italic">None specified</p>
            )}
          </CardContent>
        </Card>

        {/* Linked rescue plans */}
        {jsa.linked_rescue_plans && jsa.linked_rescue_plans.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Linked Rescue Plans</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {jsa.linked_rescue_plans.map((rp: { id: number; title: string; status: string; signed_off_at: string | null }) => (
                <div key={rp.id} className="flex items-center justify-between p-2 rounded-md border bg-gray-50">
                  <span className="text-sm font-medium">{rp.title}</span>
                  <Badge className={rp.status === 'signed_off' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}>
                    {rp.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* PPE requirements */}
        <Card>
          <CardHeader><CardTitle className="text-base">PPE Requirements</CardTitle></CardHeader>
          <CardContent>
            {jsa.ppe_requirements ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{jsa.ppe_requirements}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">None specified</p>
            )}
          </CardContent>
        </Card>

        {/* Emergency procedures */}
        <Card>
          <CardHeader><CardTitle className="text-base">Emergency Procedures</CardTitle></CardHeader>
          <CardContent>
            {jsa.emergency_procedures ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{jsa.emergency_procedures}</p>
            ) : (
              <p className="text-sm text-gray-500 italic">Not documented</p>
            )}
          </CardContent>
        </Card>

        {/* Signature form */}
        <Card>
          <CardHeader><CardTitle className="text-base">Your Signature</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signer-name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="signer-name"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signer-phone">Phone (optional)</Label>
              <Input
                id="signer-phone"
                placeholder="+1 555 000 0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <PlanInitialsFields
              plans={signPlans}
              values={planInitials}
              onChange={setPlanInitials}
              disabled={signMutation.isPending}
              timeZone={timeZone}
            />
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <PenLine className="h-4 w-4" />
                Draw your signature <span className="text-red-500">*</span>
              </Label>
              <SignaturePad
                onAccept={setSignatureData}
                onClear={() => setSignatureData('')}
                acceptDisabled={!name.trim() || !acknowledgementData}
                width={560}
                height={120}
              />
            </div>
            {signMutation.error && (
              <p className="text-sm text-red-600">
                {(signMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                  'Something went wrong. Please try again.'}
              </p>
            )}
            <Button
              className="w-full"
              disabled={!name.trim() || !acknowledgementData || !signatureData || signMutation.isPending}
              onClick={handleSign}
            >
              {signMutation.isPending ? 'Signing…' : 'I have read and understood this JSA'}
            </Button>
            <p className="text-xs text-gray-400 text-center">
              By clicking the button above you confirm that you have read and understood the hazards,
              control measures, PPE requirements, and emergency procedures in this JSA.
            </p>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
